/**
 * What the visitor allowed, read from the IAB TCF v2 `TCData` the consent
 * platform (InMobi Choice, `inMobiChoice.ts`) hands to `__tcfapi` listeners.
 *
 * The site has two consumers of consent and each maps onto standard TCF
 * purposes, not onto a platform-specific "custom vendor" (InMobi moves
 * custom vendors to its paid plan on 2027-01-31 — the mapping must keep
 * working on the free one):
 *
 * - **analytics** (Amplitude, GA4) — purpose 1 "Store and/or access
 *   information on a device" AND purpose 8 "Measure content performance".
 *   Product analytics is content measurement; without purpose 1 nothing may
 *   be stored, so cookies stay off.
 * - **advertising** (AdSense, personalised) — purposes 1, 3 "Create profiles
 *   for personalised advertising" and 4 "Use profiles to select personalised
 *   advertising". AdSense reads the TC string itself; this flag only drives
 *   Google Consent Mode for the GA4 tag.
 *
 * `gdprApplies === false` means the platform decided no consent is needed
 * (with "GDPR for users in the US region" on it asks everyone, so this is
 * rare) — everything counts as allowed.
 */
export interface TcfConsent {
  readonly analytics: boolean
  readonly advertising: boolean
}

/** The `TCData` fields this module reads (TCF v2.2 CMP API). */
export interface TcData {
  readonly gdprApplies?: boolean
  readonly eventStatus?: 'tcloaded' | 'cmpuishown' | 'useractioncomplete' | string
  readonly purpose?: { readonly consents?: Readonly<Record<string, boolean>> }
}

/** TCF v2.2 purposes 1–11 — what "Accept all" grants. */
const ALL_PURPOSES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] as const

function granted(tcData: TcData, purpose: number): boolean {
  return tcData.purpose?.consents?.[String(purpose)] === true
}

export function consentFromTcData(tcData: TcData): TcfConsent {
  if (tcData.gdprApplies === false) return { analytics: true, advertising: true }
  const storage = granted(tcData, 1)
  return {
    analytics: storage && granted(tcData, 8),
    advertising: storage && granted(tcData, 3) && granted(tcData, 4),
  }
}

/**
 * How the visitor answered the window: every purpose granted (`accepted`,
 * the "Accept all" button), none (`rejected` — the × / "Save my choices"
 * untouched), or a mix (`customized`).
 */
export type ConsentDecision = 'accepted' | 'customized' | 'rejected'

export function decisionFromTcData(tcData: TcData): ConsentDecision {
  const count = ALL_PURPOSES.filter((purpose) => granted(tcData, purpose)).length
  if (count === ALL_PURPOSES.length) return 'accepted'
  if (count === 0) return 'rejected'
  return 'customized'
}
